import type { Difficulty } from '../songpack/index.js';
import type { AutoParameters, ChartTuning, EventSources, GeneratorMode } from './ChartTuning.js';
import type { ChartDirection, SectionAnalysis, SemanticAnalysis } from './ChartDirection.js';

/**
 * The boundary between Mandalizm and the chart engine.
 *
 * Everything above this file — the CREATE SONG screen, the creation service —
 * talks to *this interface* and never to a URL. That is the whole point of it
 * existing: today the engine is a local HTTP bridge reached by `fetch`, and
 * tomorrow the same runtime wrapped in Electron or Tauri will reach the same
 * Python through IPC or a spawned process. Swapping that is a matter of
 * supplying a different implementation, not of editing a screen.
 *
 * Note what is *not* here: no analysis settings, no difficulty tuning, no notion
 * of beats or onsets as inputs. Those belong to the engine's own config file.
 * This interface asks for a Song Pack and reports what came back. Chart
 * generation stays in one place, in Python, and no part of it is reimplemented
 * on this side of the boundary.
 */

/** Audio bytes exactly as the engine should receive them. */
export interface AudioPayload {
  readonly bytes: ArrayBuffer;
  /** Only the extension is meaningful; the engine never uses the name as a path. */
  readonly fileName: string;
  readonly mimeType: string;
}

/** Cover art, already PNG. Song Pack v1 admits no other format. */
export interface CoverPayload {
  readonly bytes: ArrayBuffer;
  readonly fileName: string;
  readonly mimeType: string;
}

export interface ChartGenerationRequest {
  /** ASCII identifier, Song Pack v1 charset. See `derivePackId`. */
  readonly packId: string;
  /** The song's proper name. Becomes `manifest.displayName` verbatim. */
  readonly title: string;
  readonly artist: string;
  readonly audio: AudioPayload;
  readonly cover?: CoverPayload;
  /**
   * How the player corrected the automatic result, if they did.
   *
   * Absent means AUTO, which is what every caller written before
   * corrections existed sends, and what the engine reads as "decide
   * everything yourself".
   */
  readonly mode?: GeneratorMode;
  readonly tuning?: ChartTuning;
  /**
   * Shapes, traversals, source bias and sections.
   *
   * Absent means the engine decides placement the way it always has.
   * Present means a chunk of notes may be put on a shape instead — never
   * that a note is placed by hand.
   */
  readonly direction?: ChartDirection;
  /** Let the engine write the direction from the sections it detects. */
  readonly autoDirect?: boolean;
  /**
   * Whether that direction may read the song's structure.
   *
   * On by default. `false` asks for the intensity-only rules that existed
   * before roles did — kept reachable so the two can be compared on the same
   * audio rather than argued about.
   */
  readonly semantic?: boolean;
  /**
   * Regenerating one section: the direction the pack was last built with,
   * and which of its sections is being redone. Everything outside comes
   * from the baseline unchanged.
   */
  readonly baselineDirection?: ChartDirection;
  /**
   * The direction the engine reported for each difficulty, sent back exactly
   * as it arrived.
   *
   * Three, not one: DIRECTED AUTO balances EASY differently from HARD, so a
   * single baseline can only ever be right about one of them.
   */
  readonly resolvedDirections?: Readonly<Record<string, Record<string, unknown>>>;
  /**
   * The charts the pack already holds, as the JSON they were written as.
   *
   * This is what makes "only that section changed" a fact rather than a
   * property of the generator: the engine copies every event outside the
   * target section out of these documents instead of producing them again.
   */
  readonly baselineCharts?: Readonly<Record<string, unknown>>;
  readonly regenerateSection?: number;
}

/** One step of the engine's own progress reporting, passed through unaltered. */
export interface ChartGenerationProgress {
  readonly stage: string;
  readonly message: string;
  /** 0..1. */
  readonly fraction: number;
}

/**
 * What the engine measured and produced.
 *
 * Every number here is the engine's, read back for display and for the record.
 * None of it is recomputed on this side.
 */
export interface ChartGenerationSummary {
  readonly packId: string;
  readonly songId: string;
  readonly displayName: string;
  readonly author: string;
  readonly detectedBpm: number;
  readonly firstBeatOffsetSeconds: number;
  readonly durationSeconds: number;
  readonly sampleRate: number;
  readonly beatCount: number;
  readonly onsetCount: number;
  readonly beatTimes: readonly number[];
  readonly events: Readonly<Record<Difficulty, number>>;
  readonly coverIncluded: boolean;
  readonly elapsedSeconds: number;
  readonly archiveBytes: number;
  /** AUTO unless the request corrected something. */
  readonly generatorMode: GeneratorMode;
  readonly generatorVersion: string;
  readonly seed: number;
  /**
   * What the engine measured and what it assumed, kept apart.
   *
   * Reported for every generation, AUTO included: an automatic chart
   * whose reasoning the player cannot see is the thing they complain
   * about.
   */
  readonly autoParameters: AutoParameters | null;
  readonly appliedParameters: ChartTuning | null;
  /**
   * Per difficulty, how many events came from the beat grid, from melody
   * peaks and from onsets. The number that says whether a chart follows
   * the music or the metronome, which an event count cannot.
   */
  readonly eventSources: EventSources | null;
  /**
   * What `eventSources` counts.
   *
   * `'chart'` for a whole generation. `'section'` after one section was
   * redone: a chart spliced from a new section and a stored one has no honest
   * whole-song breakdown, because a `.chart` file does not record which layer
   * a note came from.
   */
  readonly eventSourcesScope: 'chart' | 'section';
  /** True when something was directed; false for every automatic chart. */
  readonly directed: boolean;
  /** Per difficulty, how many chunks each shape and traversal took. */
  readonly geometryUsage: EventSources | null;
  readonly patternUsage: EventSources | null;
  /**
   * The shapes in the order they were used, first chunks only.
   *
   * Reported so a player can see CIRCLE, CIRCLE, MANDALA rather than
   * infer it: a mix that never mixed would otherwise look like one that
   * did.
   */
  readonly geometrySequence: Readonly<Record<string, readonly string[]>> | null;
  /** Where the engine heard the song change. Reported for every generation. */
  readonly sectionAnalysis: SectionAnalysis | null;
  /**
   * What the engine inferred the song's parts to be, with its confidence.
   *
   * Null unless a semantic DIRECTED AUTO ran. Kept separate from
   * `sectionAnalysis`, which is measured and always true, because these are
   * guesses and a screen must be able to say which is which.
   */
  readonly semanticAnalysis: SemanticAnalysis | null;
  readonly semanticDirected: boolean;
  /** Which version of each guess produced this pack. */
  readonly analysisVersions: Readonly<Record<string, string>>;
  /** What DIRECTED AUTO decided per section, beside what it heard. */
  readonly sectionDirection: readonly Record<string, unknown>[];
  readonly autoDirected: boolean;
  /**
   * The direction the engine actually used, as it used it.
   *
   * The same document whether the player wrote it or DIRECTED AUTO did, which
   * is what lets a later section regeneration hand back exactly the direction
   * the pack was built with.
   */
  readonly appliedDirection: Record<string, unknown> | null;
  /**
   * The same thing per difficulty, which is the form a regeneration needs.
   *
   * Held and handed back verbatim. The browser never rebuilds a direction of
   * its own from what a panel happens to be showing: a baseline that changed
   * in transit is not a baseline.
   */
  readonly resolvedDirections: Readonly<Record<string, Record<string, unknown>>>;
  readonly regeneratedSection: number | null;
}

export interface GeneratedSongPack {
  readonly summary: ChartGenerationSummary;
  /** The Song Pack v1 archive, byte for byte as the engine wrote it. */
  readonly archive: ArrayBuffer;
}

export interface ChartEngineHealth {
  readonly ok: boolean;
  readonly engine: string;
  readonly bridgeVersion: string;
  readonly pythonVersion: string;
  readonly configPath: string;
  readonly maxUploadBytes: number;
  /**
   * Extensions the engine can decode on this machine, lower-case and dotted.
   *
   * Not a constant, because it depends on whether `soundfile` is installed
   * alongside it. The caller uses this to decide whether to send a file as it
   * is or to convert it first — which is how "do not re-encode pointlessly"
   * becomes a decision rather than a guess.
   */
  readonly acceptedAudio: readonly string[];
  readonly difficulties: readonly string[];
}

export interface GenerateOptions {
  readonly onProgress?: (progress: ChartGenerationProgress) => void;
  /** Aborts the generation. The engine stops at its next stage boundary. */
  readonly signal?: AbortSignal;
}

export interface ChartGenerationClient {
  /** Reports whether the engine is reachable and what it can decode. */
  health(): Promise<ChartEngineHealth>;
  generate(
    request: ChartGenerationRequest,
    options?: GenerateOptions,
  ): Promise<GeneratedSongPack>;
}

export type ChartGenerationErrorCode =
  /** Nothing answered. The engine is not running, or not where we looked. */
  | 'unreachable'
  /** The engine refused the request: bad id, empty title, oversized upload. */
  | 'rejected'
  /** The engine accepted it and then failed. Its message is carried through. */
  | 'engine-failed'
  /** The caller aborted. */
  | 'cancelled'
  /** Something answered, but not something that speaks this protocol. */
  | 'malformed-response';

/**
 * A failure with a machine-readable cause.
 *
 * The codes exist so the screen can say something useful. "The engine is not
 * running, start it with this command" and "your audio is 400 MB" are different
 * problems, and a single opaque string would make the first one look like a bug
 * in the game.
 */
export class ChartGenerationError extends Error {
  readonly code: ChartGenerationErrorCode;
  readonly status: number | null;

  constructor(
    code: ChartGenerationErrorCode,
    message: string,
    options: { status?: number; cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ChartGenerationError';
    this.code = code;
    this.status = options.status ?? null;
  }
}

export function isChartGenerationError(value: unknown): value is ChartGenerationError {
  return value instanceof ChartGenerationError;
}
